import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Day names for display
const DAYS_OF_WEEK = [
  { value: 0, name: 'Sunday', short: 'Sun' },
  { value: 1, name: 'Monday', short: 'Mon' },
  { value: 2, name: 'Tuesday', short: 'Tue' },
  { value: 3, name: 'Wednesday', short: 'Wed' },
  { value: 4, name: 'Thursday', short: 'Thu' },
  { value: 5, name: 'Friday', short: 'Fri' },
  { value: 6, name: 'Saturday', short: 'Sat' }
];

// Time options for dropdowns
const TIME_OPTIONS = [];
for (let hour = 0; hour < 24; hour++) {
  for (let minute = 0; minute < 60; minute += 30) {
    const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    TIME_OPTIONS.push(time);
  }
}

// Loading skeleton component
const AvailabilityCardSkeleton = () => (
  <div className="border border-light-border dark:border-border rounded-xl p-4 animate-pulse">
    <div className="flex justify-between items-center">
      <div className="h-5 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
      <div className="h-8 bg-slate-200 dark:bg-silver/20 rounded w-40"></div>
    </div>
  </div>
);

const DoctorAvailability = () => {
  const { user, loading: authLoading, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  // State
  const [availability, setAvailability] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [doctorId, setDoctorId] = useState(null);
  const [bufferMinutes, setBufferMinutes] = useState(0);

  useEffect(() => {
    // Redirect if not authenticated or not a doctor
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch doctor ID
  useEffect(() => {
    const fetchDoctorId = async () => {
      if (!isAuthenticated || user?.role !== 'doctor') return;

      try {
        const doctorsResponse = await fetch(`${API_URL}/doctors`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (doctorsResponse.ok) {
          const doctorsData = await doctorsResponse.json();
          const myDoctor = doctorsData.doctors?.find(
            doc => doc.firstName === user.firstName && doc.lastName === user.lastName
          );
          if (myDoctor) {
            setDoctorId(myDoctor.id);
          }
        }
      } catch (err) {
        console.error('Error fetching doctor ID:', err);
      }
    };

    fetchDoctorId();
  }, [isAuthenticated, user, token]);

  // Fetch availability
  const fetchAvailability = useCallback(async () => {
    if (!doctorId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/availability`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch availability');
      }

      const data = await response.json();

      // Convert array to object keyed by dayOfWeek
      const availabilityMap = {};
      (data.availability || []).forEach(slot => {
        availabilityMap[slot.dayOfWeek] = {
          isActive: slot.isActive,
          startTime: slot.startTime?.substring(0, 5) || '09:00',
          endTime: slot.endTime?.substring(0, 5) || '17:00',
          bufferMinutes: slot.bufferMinutes || 0
        };
        // Use the buffer from the first active slot
        if (slot.isActive && !bufferMinutes) {
          setBufferMinutes(slot.bufferMinutes || 0);
        }
      });

      setAvailability(availabilityMap);
    } catch (err) {
      console.error('Error fetching availability:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [doctorId, token, bufferMinutes]);

  useEffect(() => {
    if (doctorId) {
      fetchAvailability();
    }
  }, [doctorId, fetchAvailability]);

  // Toggle day active/inactive
  const toggleDay = (dayValue) => {
    setAvailability(prev => {
      const current = prev[dayValue];
      if (current) {
        // Toggle isActive if already exists
        return {
          ...prev,
          [dayValue]: {
            ...current,
            isActive: !current.isActive
          }
        };
      } else {
        // Add new day with default times
        return {
          ...prev,
          [dayValue]: {
            isActive: true,
            startTime: '09:00',
            endTime: '17:00',
            bufferMinutes: bufferMinutes
          }
        };
      }
    });
  };

  // Update start time
  const updateStartTime = (dayValue, time) => {
    setAvailability(prev => ({
      ...prev,
      [dayValue]: {
        ...prev[dayValue],
        startTime: time
      }
    }));
  };

  // Update end time
  const updateEndTime = (dayValue, time) => {
    setAvailability(prev => ({
      ...prev,
      [dayValue]: {
        ...prev[dayValue],
        endTime: time
      }
    }));
  };

  // Validate time range - returns error message or null if valid
  const validateTimeRange = (startTime, endTime) => {
    // Parse times to compare (HH:MM format)
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    if (endMinutes <= startMinutes) {
      return 'End time must be after start time';
    }
    return null;
  };

  // Save availability
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Validate all active slots have valid time ranges
      for (const [dayValue, slot] of Object.entries(availability)) {
        if (slot.isActive) {
          const dayName = DAYS_OF_WEEK.find(d => d.value === parseInt(dayValue))?.name || `Day ${dayValue}`;
          const timeError = validateTimeRange(slot.startTime, slot.endTime);
          if (timeError) {
            throw new Error(`${dayName}: ${timeError}`);
          }
        }
      }

      // Convert to array format
      const availabilityArray = Object.entries(availability)
        .filter(([_, slot]) => slot.isActive)
        .map(([dayOfWeek, slot]) => ({
          dayOfWeek: parseInt(dayOfWeek),
          startTime: slot.startTime,
          endTime: slot.endTime,
          bufferMinutes: bufferMinutes,
          isActive: true
        }));

      const response = await fetch(`${API_URL}/doctors/${doctorId}/availability`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ availability: availabilityArray })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save availability');
      }

      // Show success toast notification
      toast.success('Availability saved successfully!');
    } catch (err) {
      console.error('Error saving availability:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <Link
              to="/doctor/dashboard"
              className="flex items-center gap-2 text-light-text-secondary dark:text-silver/60 hover:text-light-text dark:hover:text-silver mb-4 transition-smooth"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </Link>
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
              Set Availability
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              Configure your working hours for each day of the week
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-availability-button"
          >
            {isSaving ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl" data-testid="success-message">
            <p className="text-green-400">{successMessage}</p>
          </div>
        )}

        {/* Buffer Time Setting */}
        <div className="mb-6 glass border border-light-border dark:border-border rounded-2xl p-6">
          <label htmlFor="buffer-time" className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4 block">
            Buffer Time Between Appointments
          </label>
          <div className="flex items-center gap-4">
            <select
              id="buffer-time"
              value={bufferMinutes}
              onChange={(e) => setBufferMinutes(parseInt(e.target.value))}
              className="input-field w-48"
              data-testid="buffer-time-select"
            >
              <option value="0">No buffer</option>
              <option value="5">5 minutes</option>
              <option value="10">10 minutes</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
            </select>
            <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
              Time between appointments for preparation
            </p>
          </div>
        </div>

        {/* Weekly Schedule */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Weekly Schedule
          </h2>

          {isLoading ? (
            <div className="space-y-4">
              {DAYS_OF_WEEK.map(day => (
                <AvailabilityCardSkeleton key={day.value} />
              ))}
            </div>
          ) : (
            <div className="space-y-4" data-testid="availability-list">
              {DAYS_OF_WEEK.map(day => {
                const dayAvailability = availability[day.value];
                const isActive = dayAvailability?.isActive || false;

                return (
                  <div
                    key={day.value}
                    className={`border rounded-xl p-4 transition-smooth ${
                      isActive ? 'border-light-border dark:border-border hover:border-slate-400 dark:hover:border-white/20' : 'border-light-border/50 dark:border-border/50 bg-slate-100 dark:bg-silver/5'
                    }`}
                    data-testid={`day-${day.value}`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      {/* Day name and toggle */}
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleDay(day.value)}
                          className={`w-12 h-6 rounded-full transition-smooth relative ${
                            isActive ? 'bg-green-500' : 'bg-silver/20'
                          }`}
                          data-testid={`toggle-${day.value}`}
                          aria-label={`Toggle ${day.name}`}
                        >
                          <div
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-smooth ${
                              isActive ? 'left-7' : 'left-1'
                            }`}
                          />
                        </button>
                        <span className={`font-body font-medium w-28 ${isActive ? 'text-light-text dark:text-pure-white' : 'text-slate-400 dark:text-silver/40'}`}>
                          {day.name}
                        </span>
                      </div>

                      {/* Time selectors */}
                      {isActive && (
                        <div className="flex items-center gap-2">
                          <select
                            id={`start-time-${day.value}`}
                            value={dayAvailability?.startTime || '09:00'}
                            onChange={(e) => updateStartTime(day.value, e.target.value)}
                            className="input-field py-2 px-3 w-28"
                            aria-label={`${day.name} start time`}
                            data-testid={`start-time-${day.value}`}
                          >
                            {TIME_OPTIONS.map(time => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                          <span className="text-light-text-secondary dark:text-silver/60">to</span>
                          <select
                            id={`end-time-${day.value}`}
                            value={dayAvailability?.endTime || '17:00'}
                            onChange={(e) => updateEndTime(day.value, e.target.value)}
                            className="input-field py-2 px-3 w-28"
                            aria-label={`${day.name} end time`}
                            data-testid={`end-time-${day.value}`}
                          >
                            {TIME_OPTIONS.map(time => (
                              <option key={time} value={time}>{time}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Not working indicator */}
                      {!isActive && (
                        <span className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40">
                          Not working
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/30 rounded-2xl">
          <h3 className="font-mono text-sm uppercase tracking-widest text-blue-400 mb-2">
            How Availability Works
          </h3>
          <ul className="font-body text-sm text-slate-600 dark:text-silver/80 space-y-2">
            <li>
              <span className="text-light-text dark:text-pure-white">1.</span> Toggle each day on/off to set your working days
            </li>
            <li>
              <span className="text-light-text dark:text-pure-white">2.</span> Set start and end times for each working day
            </li>
            <li>
              <span className="text-light-text dark:text-pure-white">3.</span> Buffer time adds breaks between appointments
            </li>
            <li>
              <span className="text-light-text dark:text-pure-white">4.</span> Patients will only see available slots during your working hours
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DoctorAvailability;
